import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { ArrowLeft } from 'lucide-react';
import { useTenantConfig } from '../../contexts/TenantConfigContext';
import PortcoDebtLifecycleModal, { LifecycleAction } from './PortcoDebtLifecycleModal';
import PortcoMarkModal from './PortcoMarkModal';
import {
  PortcoGlobal,
  Surface,
  Header,
  HeaderInner,
  HeaderBrand,
  HeaderBrandMark,
  HeaderCrumb,
  HeaderActions,
  ContextStrip,
  ContextStripInner,
  ContextName,
  ContextNameTitle,
  ContextNameSub,
  ContextField,
  ContextFieldLabel,
  ContextFieldValue,
  TabBar,
  TabBarInner,
  Tab,
  BodyNarrow,
  Panel,
  PanelHead,
  PanelTitle,
  PanelMeta,
  PanelBody,
  Table,
  Thead,
  Th,
  Td,
  TdMono,
  Tr,
  StatusPill,
  Loading,
  Empty,
  portcoTokens,
} from './PortcoTheme';

interface PortfolioCompany {
  id: number;
  companyName: string;
  sector?: string;
  industry?: string;
  country?: string;
  ownershipPct?: number;
  status?: string;
  syntheticCusip?: string;
  fundId?: number;
  leadPartner?: string;
}

interface PortcoKpi {
  id: number;
  periodEnd: string;
  revenue?: number;
  ebitda?: number;
  netDebt?: number;
  headcount?: number;
  source?: string;
}

interface PortcoValuation {
  id: number;
  markDate: string;
  markType?: string;
  equityValue?: number;
  debtValue?: number;
  enterpriseValue?: number;
  multipleEbitda?: number;
  multipleRevenue?: number;
}

interface PortcoDebt {
  id: number;
  instrumentType?: string;
  principal?: number;
  drawn?: number;
  undrawn?: number;
  currency?: string;
  rateType?: string;
  spreadBps?: number;
  baseRate?: string;
  maturity?: string;
  seniority?: string;
  lender?: string;
  syntheticCusip?: string;
}

interface CovenantDefinition {
  id: number;
  covenantType?: string;
  covenantName?: string;
  covenantCategory?: string;
  metricType?: string;
  thresholdType?: string;
  thresholdValue?: number;
  comparator?: string;
  testFrequency?: string;
  measurementFrequency?: string;
  isActive?: boolean;
  portcoDebtId?: number;
}

type TrendDirection = 'IMPROVING' | 'STABLE' | 'DETERIORATING' | 'INSUFFICIENT_DATA';

interface CovenantTrendPoint {
  testDate: string;
  value: number;
  threshold: number;
  cushion: number;
  status: string;
}

interface CovenantTrend {
  covenantId: number;
  covenantName: string;
  direction: TrendDirection;
  rateOfChange: number;
  quartersAnalyzed: number;
  history: CovenantTrendPoint[];
  estimatedQuartersToBreach: number | null;
}

type CovenantDraft = {
  covenantName: string;
  covenantType: 'FINANCIAL' | 'AFFIRMATIVE' | 'NEGATIVE' | 'REPORTING';
  covenantCategory:
    | 'LEVERAGE'
    | 'COVERAGE'
    | 'LIQUIDITY'
    | 'CAPEX'
    | 'RESTRICTED_PAYMENTS'
    | 'ASSET_SALE'
    | 'DEBT_INCURRENCE'
    | '';
  metricType:
    | 'TOTAL_LEVERAGE'
    | 'SENIOR_LEVERAGE'
    | 'NET_LEVERAGE'
    | 'INTEREST_COVERAGE'
    | 'FIXED_CHARGE'
    | 'DEBT_TO_EBITDA'
    | 'DEBT_SERVICE_COVERAGE'
    | 'LIQUIDITY'
    | 'CAPEX'
    | 'CASH_FLOW'
    | 'LOAN_TO_VALUE'
    | 'DEBT_YIELD'
    | '';
  thresholdType: 'MAX' | 'MIN' | 'RANGE';
  thresholdValue: string;
  measurementFrequency: 'QUARTERLY' | 'SEMI_ANNUAL' | 'ANNUAL';
};

const COVENANT_DRAFT_DEFAULT: CovenantDraft = {
  covenantName: '',
  covenantType: 'FINANCIAL',
  covenantCategory: 'LEVERAGE',
  metricType: 'TOTAL_LEVERAGE',
  thresholdType: 'MAX',
  thresholdValue: '',
  measurementFrequency: 'QUARTERLY',
};

type TabKey = 'overview' | 'kpis' | 'valuations' | 'debt' | 'covenants' | 'documents';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'kpis', label: 'KPIs' },
  { key: 'valuations', label: 'Valuations' },
  { key: 'debt', label: 'Debt' },
  { key: 'covenants', label: 'Covenants' },
  { key: 'documents', label: 'Documents' },
];

const fmtNum = (v?: number, dp = 0) =>
  v == null ? '—' : v.toLocaleString(undefined, { maximumFractionDigits: dp, minimumFractionDigits: dp });

const fmtMoney = (v?: number, currency = 'USD') => {
  if (v == null) return '—';
  return `${currency} ${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const fmtPct = (v?: number) => (v == null ? '—' : `${v.toFixed(1)}%`);

const fmtDate = (s?: string) => (s ? s.slice(0, 10) : '—');

const HeaderBackLink = styled.button`
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: rgba(255, 255, 255, 0.85);
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 6px 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  border-radius: 3px;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.35);
  }
`;

// ── Covenant inline-form styling ───────────────────────────────────────────
// Slate-on-paper to read in the institutional-finance idiom (cf. PGIM detail
// pages / Aladdin) — quiet borders, monospace-friendly inputs, no rounded
// pill-buttons. Consciously *not* introducing a generic <FormGrid/> primitive
// in PortcoTheme until a second screen actually needs it; per
// `feedback_no_speculative_granularity` keep it local.
const CovenantFormBlock = styled.div`
  border-top: 1px solid ${portcoTokens.hairline};
  background: ${portcoTokens.surfaceMuted};
  padding: 16px 20px 14px;
`;

const CovenantFormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px 14px;
`;

const CovenantField = styled.div<{ $span?: number }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  grid-column: span ${(p) => p.$span ?? 1};
`;

const CovenantLabel = styled.label`
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${portcoTokens.inkMuted};
`;

const CovenantInput = styled.input`
  font: inherit;
  font-size: 13px;
  background: #fff;
  border: 1px solid ${portcoTokens.hairline};
  padding: 6px 8px;
  color: ${portcoTokens.inkBody};
  &:focus {
    outline: none;
    border-color: ${portcoTokens.accent};
  }
`;

const CovenantSelect = styled.select`
  font: inherit;
  font-size: 13px;
  background: #fff;
  border: 1px solid ${portcoTokens.hairline};
  padding: 6px 8px;
  color: ${portcoTokens.inkBody};
  &:focus {
    outline: none;
    border-color: ${portcoTokens.accent};
  }
`;

const CovenantFormFoot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
`;

const CovenantErr = styled.div`
  margin-top: 10px;
  font-size: 12px;
  color: #b91c1c;
`;

const covenantAddBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: `1px solid ${portcoTokens.hairline}`,
  color: portcoTokens.inkBody,
  fontSize: 11,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  padding: '4px 10px',
  cursor: 'pointer',
};

const covenantPrimaryBtnStyle: React.CSSProperties = {
  background: portcoTokens.inkPrimary,
  border: `1px solid ${portcoTokens.inkPrimary}`,
  color: '#fff',
  fontSize: 11,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  padding: '6px 14px',
  cursor: 'pointer',
};

const covenantSecondaryBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: `1px solid ${portcoTokens.hairline}`,
  color: portcoTokens.inkBody,
  fontSize: 11,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  padding: '6px 14px',
  cursor: 'pointer',
};

/**
 * Embedded mode (`embedded={true}`) is used when this screen is hosted inside
 * the Alternatives → Portfolio Companies workbench. It suppresses the outer
 * AxiomFlow brand bar + breadcrumb (workbench provides nav context) and skips
 * the global style injection. `portcoIdOverride` lets the workbench pass the
 * portco id via URL query param instead of route param.
 * Light-vs-dark chrome harmonization is Task #10.
 */
interface PortcoDetailScreenProps {
  embedded?: boolean;
  portcoIdOverride?: string;
}

const PortcoDetailScreen: React.FC<PortcoDetailScreenProps> = ({ embedded = false, portcoIdOverride }) => {
  const params = useParams<{ id: string }>();
  const id = portcoIdOverride ?? params.id;
  const navigate = useNavigate();
  const { tenantName } = useTenantConfig();
  const [tab, setTab] = useState<TabKey>('overview');

  const [portco, setPortco] = useState<PortfolioCompany | null>(null);
  const [kpis, setKpis] = useState<PortcoKpi[]>([]);
  const [valuations, setValuations] = useState<PortcoValuation[]>([]);
  const [debts, setDebts] = useState<PortcoDebt[]>([]);
  const [covenantsByDebt, setCovenantsByDebt] = useState<Record<number, CovenantDefinition[]>>({});
  const [trendByCovenantId, setTrendByCovenantId] = useState<Record<number, CovenantTrend | null>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyDebtId, setBusyDebtId] = useState<number | null>(null);
  const [lifecycleModal, setLifecycleModal] = useState<{ action: LifecycleAction; debt: PortcoDebt } | null>(null);
  const [markModalOpen, setMarkModalOpen] = useState(false);
  const [addingCovenantForDebtId, setAddingCovenantForDebtId] = useState<number | null>(null);
  const [covenantDraft, setCovenantDraft] = useState<CovenantDraft>(COVENANT_DRAFT_DEFAULT);
  const [savingCovenant, setSavingCovenant] = useState(false);
  const [covenantError, setCovenantError] = useState<string | null>(null);

  const loadTrendsFor = async (covs: CovenantDefinition[]) => {
    const updates: Record<number, CovenantTrend | null> = {};
    await Promise.all(
      covs.map(async (c) => {
        try {
          const r = await axios.get<CovenantTrend>(`/api/portco/covenants/${c.id}/trend?lookbackQuarters=4`);
          updates[c.id] = r.data;
        } catch {
          updates[c.id] = null;
        }
      })
    );
    setTrendByCovenantId((prev) => ({ ...prev, ...updates }));
  };

  const reloadCovenantsForDebt = async (debtId: number) => {
    try {
      const r = await axios.get<CovenantDefinition[]>(`/api/portco/debt/${debtId}/covenants`);
      const covs = r.data || [];
      setCovenantsByDebt((prev) => ({ ...prev, [debtId]: covs }));
      void loadTrendsFor(covs);
    } catch {
      // leave existing
    }
  };

  const openAddCovenant = (debtId: number) => {
    setAddingCovenantForDebtId(debtId);
    setCovenantDraft(COVENANT_DRAFT_DEFAULT);
    setCovenantError(null);
  };

  const cancelAddCovenant = () => {
    setAddingCovenantForDebtId(null);
    setCovenantDraft(COVENANT_DRAFT_DEFAULT);
    setCovenantError(null);
  };

  const submitCovenant = async (debtId: number) => {
    setCovenantError(null);
    if (!covenantDraft.covenantName.trim()) {
      setCovenantError('Covenant name is required');
      return;
    }
    const threshold = parseFloat(covenantDraft.thresholdValue);
    if (!Number.isFinite(threshold)) {
      setCovenantError('Threshold value must be a number');
      return;
    }
    setSavingCovenant(true);
    try {
      await axios.post(`/api/portco/debt/${debtId}/covenants`, {
        covenantName: covenantDraft.covenantName.trim(),
        covenantType: covenantDraft.covenantType,
        covenantCategory: covenantDraft.covenantCategory || null,
        metricType: covenantDraft.metricType || null,
        thresholdType: covenantDraft.thresholdType,
        thresholdValue: threshold,
        measurementFrequency: covenantDraft.measurementFrequency,
        isActive: true,
      });
      await reloadCovenantsForDebt(debtId);
      setAddingCovenantForDebtId(null);
      setCovenantDraft(COVENANT_DRAFT_DEFAULT);
    } catch (e: any) {
      setCovenantError(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSavingCovenant(false);
    }
  };

  const reloadDebts = async () => {
    if (!id) return;
    try {
      const r = await axios.get<PortcoDebt[]>(`/api/portco/${id}/debt`);
      setDebts(r.data || []);
    } catch {
      // leave existing list intact
    }
  };

  const handleDraw = (d: PortcoDebt) => {
    setLifecycleModal({ action: 'DRAW', debt: d });
  };

  const handleRepay = (d: PortcoDebt) => {
    setLifecycleModal({ action: 'REPAY', debt: d });
  };

  const handleLifecycleSuccess = async () => {
    if (lifecycleModal) setBusyDebtId(lifecycleModal.debt.id);
    setLifecycleModal(null);
    try {
      await reloadDebts();
    } finally {
      setBusyDebtId(null);
    }
  };

  const reloadValuations = async () => {
    if (!id) return;
    try {
      const r = await axios.get<PortcoValuation[]>(`/api/portco/${id}/valuations`);
      setValuations(r.data || []);
    } catch {
      // leave existing
    }
  };

  const handleMarkSuccess = async () => {
    setMarkModalOpen(false);
    await reloadValuations();
  };

  const debtBtnStyle = (variant: 'draw' | 'repay'): React.CSSProperties => ({
    marginRight: 6,
    padding: '3px 10px',
    border: `1px solid ${variant === 'draw' ? '#1e3a8a' : '#475569'}`,
    background: variant === 'draw' ? '#1e3a8a' : '#fff',
    color: variant === 'draw' ? '#fff' : '#1e293b',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    cursor: 'pointer',
  });

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [pc, kpiRes, valRes] = await Promise.all([
          axios.get<PortfolioCompany>(`/api/portco/${id}`),
          axios.get<PortcoKpi[]>(`/api/portco/${id}/kpi`),
          axios.get<PortcoValuation[]>(`/api/portco/${id}/valuations`),
        ]);
        if (!mounted) return;
        setPortco(pc.data);
        setKpis(kpiRes.data || []);
        setValuations(valRes.data || []);

        // Debt list isn't directly exposed per portco; pull all debt rows for this tenant
        // and filter — but the controller only exposes covenants per debtId.
        // Use a portfolio-companies-with-debt fan-out endpoint if present.
        try {
          const debtRes = await axios.get<PortcoDebt[]>(`/api/portco/${id}/debt`);
          if (!mounted) return;
          setDebts(debtRes.data || []);
        } catch {
          if (mounted) setDebts([]);
        }
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.response?.data?.message || e?.message || 'Failed to load portfolio company');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id]);

  // Lazy-load covenants per debt as user opens the Covenants tab
  useEffect(() => {
    if (tab !== 'covenants' || debts.length === 0) return;
    let mounted = true;
    (async () => {
      const map: Record<number, CovenantDefinition[]> = {};
      await Promise.all(
        debts.map(async (d) => {
          try {
            const r = await axios.get<CovenantDefinition[]>(`/api/portco/debt/${d.id}/covenants`);
            map[d.id] = r.data || [];
          } catch {
            map[d.id] = [];
          }
        }),
      );
      if (mounted) {
        setCovenantsByDebt(map);
        const allCovs = Object.values(map).flat();
        if (allCovs.length > 0) void loadTrendsFor(allCovs);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, debts]);

  const latestKpi = useMemo(() => kpis[0], [kpis]);
  const latestValuation = useMemo(() => valuations[0], [valuations]);
  const totalDrawn = useMemo(
    () => debts.reduce((s, d) => s + (d.drawn || 0), 0),
    [debts],
  );

  return (
    <>
      {!embedded && <PortcoGlobal />}
      <Surface className="portco-surface">
        {!embedded && (
          <Header>
            <HeaderInner>
              <HeaderBrand>
                <HeaderBrandMark>AF</HeaderBrandMark>
                <span>AxiomFlow / Portfolio Companies</span>
              </HeaderBrand>
              <HeaderCrumb>
                <a
                  href="/portco"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/portco');
                  }}
                  style={{ color: portcoTokens.inkOnDarkMuted }}
                >
                  Portfolio Companies
                </a>
                {' / '}
                {portco?.companyName || `#${id}`}
              </HeaderCrumb>
              <HeaderActions>
                <HeaderBackLink
                  onClick={() => navigate('/portco')}
                  title="Back to portfolio companies"
                >
                  <ArrowLeft size={12} />
                  All companies
                </HeaderBackLink>
                <HeaderBackLink
                  onClick={() => navigate('/portfolio-management?tab=alternatives&sub=private-equity')}
                  title="Return to Alternatives"
                >
                  <ArrowLeft size={12} />
                  Alternatives
                </HeaderBackLink>
                <span>Tenant: {tenantName}</span>
              </HeaderActions>
            </HeaderInner>
          </Header>
        )}

        {loading && !portco && <Loading>Loading portfolio company…</Loading>}
        {error && !loading && <Empty>Could not load. {error}</Empty>}

        {portco && (
          <>
            <ContextStrip>
              <ContextStripInner>
                <ContextName>
                  <ContextNameTitle>{portco.companyName}</ContextNameTitle>
                  <ContextNameSub>
                    {portco.syntheticCusip || `#${portco.id}`}
                    {portco.sector && ` · ${portco.sector}`}
                    {portco.country && ` · ${portco.country}`}
                  </ContextNameSub>
                </ContextName>
                <ContextField>
                  <ContextFieldLabel>Status</ContextFieldLabel>
                  <ContextFieldValue>{portco.status || 'ACTIVE'}</ContextFieldValue>
                </ContextField>
                <ContextField>
                  <ContextFieldLabel>Ownership</ContextFieldLabel>
                  <ContextFieldValue>{fmtPct(portco.ownershipPct)}</ContextFieldValue>
                </ContextField>
                <ContextField>
                  <ContextFieldLabel>Fund</ContextFieldLabel>
                  <ContextFieldValue>
                    {portco.fundId != null ? `#${portco.fundId}` : '—'}
                  </ContextFieldValue>
                </ContextField>
                <ContextField>
                  <ContextFieldLabel>Last EBITDA</ContextFieldLabel>
                  <ContextFieldValue>{fmtNum(latestKpi?.ebitda)}</ContextFieldValue>
                </ContextField>
                <ContextField>
                  <ContextFieldLabel>Total drawn</ContextFieldLabel>
                  <ContextFieldValue>{fmtNum(totalDrawn)}</ContextFieldValue>
                </ContextField>
              </ContextStripInner>
            </ContextStrip>

            <TabBar>
              <TabBarInner>
                {TABS.map((t) => (
                  <Tab key={t.key} $active={tab === t.key} onClick={() => setTab(t.key)}>
                    {t.label}
                  </Tab>
                ))}
              </TabBarInner>
            </TabBar>

            <BodyNarrow>
              {tab === 'overview' && (
                <>
                  <Panel>
                    <PanelHead>
                      <PanelTitle>Latest snapshot</PanelTitle>
                      <PanelMeta>
                        {latestKpi?.periodEnd ? `KPI period · ${fmtDate(latestKpi.periodEnd)}` : 'No KPI yet'}
                      </PanelMeta>
                    </PanelHead>
                    <PanelBody>
                      <Table>
                        <Thead>
                          <tr>
                            <Th>Metric</Th>
                            <Th style={{ textAlign: 'right' }}>Value</Th>
                            <Th style={{ textAlign: 'right' }}>As of</Th>
                          </tr>
                        </Thead>
                        <tbody>
                          <Tr>
                            <Td>Revenue (LTM)</Td>
                            <TdMono style={{ textAlign: 'right' }}>{fmtNum(latestKpi?.revenue)}</TdMono>
                            <TdMono style={{ textAlign: 'right' }}>{fmtDate(latestKpi?.periodEnd)}</TdMono>
                          </Tr>
                          <Tr>
                            <Td>EBITDA (LTM)</Td>
                            <TdMono style={{ textAlign: 'right' }}>{fmtNum(latestKpi?.ebitda)}</TdMono>
                            <TdMono style={{ textAlign: 'right' }}>{fmtDate(latestKpi?.periodEnd)}</TdMono>
                          </Tr>
                          <Tr>
                            <Td>Net debt</Td>
                            <TdMono style={{ textAlign: 'right' }}>{fmtNum(latestKpi?.netDebt)}</TdMono>
                            <TdMono style={{ textAlign: 'right' }}>{fmtDate(latestKpi?.periodEnd)}</TdMono>
                          </Tr>
                          <Tr>
                            <Td>Headcount</Td>
                            <TdMono style={{ textAlign: 'right' }}>{fmtNum(latestKpi?.headcount)}</TdMono>
                            <TdMono style={{ textAlign: 'right' }}>{fmtDate(latestKpi?.periodEnd)}</TdMono>
                          </Tr>
                          <Tr>
                            <Td>Latest mark — Equity value</Td>
                            <TdMono style={{ textAlign: 'right' }}>
                              {fmtNum(latestValuation?.equityValue)}
                            </TdMono>
                            <TdMono style={{ textAlign: 'right' }}>
                              {fmtDate(latestValuation?.markDate)}
                            </TdMono>
                          </Tr>
                          <Tr>
                            <Td>Latest mark — Enterprise value</Td>
                            <TdMono style={{ textAlign: 'right' }}>
                              {fmtNum(latestValuation?.enterpriseValue)}
                            </TdMono>
                            <TdMono style={{ textAlign: 'right' }}>
                              {fmtDate(latestValuation?.markDate)}
                            </TdMono>
                          </Tr>
                        </tbody>
                      </Table>
                    </PanelBody>
                  </Panel>
                </>
              )}

              {tab === 'kpis' && (
                <Panel>
                  <PanelHead>
                    <PanelTitle>KPI history</PanelTitle>
                    <PanelMeta>{kpis.length} period{kpis.length === 1 ? '' : 's'}</PanelMeta>
                  </PanelHead>
                  <PanelBody style={{ padding: 0 }}>
                    {kpis.length === 0 ? (
                      <Empty>No KPI history recorded yet.</Empty>
                    ) : (
                      <Table>
                        <Thead>
                          <tr>
                            <Th>Period end</Th>
                            <Th style={{ textAlign: 'right' }}>Revenue</Th>
                            <Th style={{ textAlign: 'right' }}>EBITDA</Th>
                            <Th style={{ textAlign: 'right' }}>Net debt</Th>
                            <Th style={{ textAlign: 'right' }}>Headcount</Th>
                            <Th>Source</Th>
                          </tr>
                        </Thead>
                        <tbody>
                          {kpis.map((k) => (
                            <Tr key={k.id}>
                              <TdMono>{fmtDate(k.periodEnd)}</TdMono>
                              <TdMono style={{ textAlign: 'right' }}>{fmtNum(k.revenue)}</TdMono>
                              <TdMono style={{ textAlign: 'right' }}>{fmtNum(k.ebitda)}</TdMono>
                              <TdMono style={{ textAlign: 'right' }}>{fmtNum(k.netDebt)}</TdMono>
                              <TdMono style={{ textAlign: 'right' }}>{fmtNum(k.headcount)}</TdMono>
                              <Td>{k.source || '—'}</Td>
                            </Tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </PanelBody>
                </Panel>
              )}

              {tab === 'valuations' && (
                <Panel>
                  <PanelHead>
                    <PanelTitle>Valuation history</PanelTitle>
                    <PanelMeta>
                      {valuations.length} mark{valuations.length === 1 ? '' : 's'}
                      <button
                        onClick={() => setMarkModalOpen(true)}
                        style={{
                          marginLeft: 12,
                          padding: '4px 10px',
                          background: portcoTokens.accent,
                          color: '#fff',
                          border: 0,
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          cursor: 'pointer',
                        }}
                      >
                        + New mark
                      </button>
                    </PanelMeta>
                  </PanelHead>
                  <PanelBody style={{ padding: 0 }}>
                    {valuations.length === 0 ? (
                      <Empty>No valuation marks yet.</Empty>
                    ) : (
                      <Table>
                        <Thead>
                          <tr>
                            <Th>Mark date</Th>
                            <Th>Type</Th>
                            <Th style={{ textAlign: 'right' }}>Equity</Th>
                            <Th style={{ textAlign: 'right' }}>Debt</Th>
                            <Th style={{ textAlign: 'right' }}>EV</Th>
                            <Th style={{ textAlign: 'right' }}>EV/EBITDA</Th>
                            <Th style={{ textAlign: 'right' }}>EV/Rev</Th>
                          </tr>
                        </Thead>
                        <tbody>
                          {valuations.map((v) => (
                            <Tr key={v.id}>
                              <TdMono>{fmtDate(v.markDate)}</TdMono>
                              <Td>{v.markType || '—'}</Td>
                              <TdMono style={{ textAlign: 'right' }}>{fmtNum(v.equityValue)}</TdMono>
                              <TdMono style={{ textAlign: 'right' }}>{fmtNum(v.debtValue)}</TdMono>
                              <TdMono style={{ textAlign: 'right' }}>{fmtNum(v.enterpriseValue)}</TdMono>
                              <TdMono style={{ textAlign: 'right' }}>
                                {v.multipleEbitda != null ? `${v.multipleEbitda.toFixed(1)}x` : '—'}
                              </TdMono>
                              <TdMono style={{ textAlign: 'right' }}>
                                {v.multipleRevenue != null ? `${v.multipleRevenue.toFixed(1)}x` : '—'}
                              </TdMono>
                            </Tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </PanelBody>
                </Panel>
              )}

              {tab === 'debt' && (
                <Panel>
                  <PanelHead>
                    <PanelTitle>Sponsor-finance debt</PanelTitle>
                    <PanelMeta>
                      {debts.length} facilit{debts.length === 1 ? 'y' : 'ies'}
                      <button
                        onClick={() => navigate(`/trade-entry/portco-debt?portcoId=${id}`)}
                        style={{
                          marginLeft: 12,
                          padding: '4px 10px',
                          background: portcoTokens.accent,
                          color: '#fff',
                          border: 0,
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          cursor: 'pointer',
                        }}
                      >
                        + New facility
                      </button>
                    </PanelMeta>
                  </PanelHead>
                  <PanelBody style={{ padding: 0 }}>
                    {debts.length === 0 ? (
                      <Empty>No portco debt recorded yet.</Empty>
                    ) : (
                      <Table>
                        <Thead>
                          <tr>
                            <Th>CUSIP</Th>
                            <Th>Type</Th>
                            <Th>Lender</Th>
                            <Th style={{ textAlign: 'right' }}>Principal</Th>
                            <Th style={{ textAlign: 'right' }}>Drawn</Th>
                            <Th style={{ textAlign: 'right' }}>Undrawn</Th>
                            <Th>Rate</Th>
                            <Th>Maturity</Th>
                            <Th>Lifecycle</Th>
                          </tr>
                        </Thead>
                        <tbody>
                          {debts.map((d) => (
                            <Tr key={d.id}>
                              <TdMono>{d.syntheticCusip || `#${d.id}`}</TdMono>
                              <Td>{d.instrumentType || '—'}</Td>
                              <Td>{d.lender || '—'}</Td>
                              <TdMono style={{ textAlign: 'right' }}>
                                {fmtMoney(d.principal, d.currency)}
                              </TdMono>
                              <TdMono style={{ textAlign: 'right' }}>{fmtNum(d.drawn)}</TdMono>
                              <TdMono style={{ textAlign: 'right' }}>{fmtNum(d.undrawn)}</TdMono>
                              <TdMono>
                                {d.rateType === 'FLOAT'
                                  ? `${d.baseRate || ''}+${d.spreadBps ?? 0}bps`
                                  : d.rateType || '—'}
                              </TdMono>
                              <TdMono>{fmtDate(d.maturity)}</TdMono>
                              <Td>
                                <button
                                  onClick={() => handleDraw(d)}
                                  disabled={busyDebtId === d.id}
                                  style={debtBtnStyle('draw')}
                                >
                                  Draw
                                </button>
                                <button
                                  onClick={() => handleRepay(d)}
                                  disabled={busyDebtId === d.id}
                                  style={debtBtnStyle('repay')}
                                >
                                  Repay
                                </button>
                              </Td>
                            </Tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </PanelBody>
                </Panel>
              )}

              {tab === 'covenants' && (
                <>
                  {/* Bridge into the unified Covenant Center — single master view
                      across leveraged loans, private credit, RE debt, and portco
                      debt (Task #13). The per-debt inline create below stays for
                      focused workflow; the link surfaces the cross-source picture. */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                    <button
                      type="button"
                      onClick={() => navigate('/portfolio-management?tab=credit-loans&sub=covenants&scope=PORTCO_DEBT')}
                      style={{
                        background: 'rgba(139, 92, 246, 0.12)',
                        border: '1px solid rgba(139, 92, 246, 0.4)',
                        color: '#c4b5fd',
                        padding: '8px 14px',
                        borderRadius: 8,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      View all portco-debt covenants in Covenant Center →
                    </button>
                  </div>
                  {debts.length === 0 ? (
                    <Empty>
                      Covenants attach to sponsor-finance debt — record a facility on the Debt tab first.
                    </Empty>
                  ) : (
                    debts.map((d) => {
                      const covs = covenantsByDebt[d.id] || [];
                      return (
                        <Panel key={d.id}>
                          <PanelHead>
                            <PanelTitle>
                              {d.lender || 'Lender'} · {d.instrumentType || 'FACILITY'} ·{' '}
                              <span style={{ fontFamily: portcoTokens.fontMono, fontWeight: 400 }}>
                                {d.syntheticCusip || `#${d.id}`}
                              </span>
                            </PanelTitle>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <PanelMeta>{covs.length} covenant{covs.length === 1 ? '' : 's'}</PanelMeta>
                              {addingCovenantForDebtId === d.id ? null : (
                                <button
                                  type="button"
                                  onClick={() => openAddCovenant(d.id)}
                                  style={covenantAddBtnStyle}
                                >
                                  + Add covenant
                                </button>
                              )}
                            </div>
                          </PanelHead>
                          {addingCovenantForDebtId === d.id && (
                            <CovenantFormBlock>
                              <CovenantFormGrid>
                                <CovenantField $span={2}>
                                  <CovenantLabel>Covenant name</CovenantLabel>
                                  <CovenantInput
                                    value={covenantDraft.covenantName}
                                    onChange={(e) =>
                                      setCovenantDraft({ ...covenantDraft, covenantName: e.target.value })
                                    }
                                    placeholder="e.g. Net Leverage ≤ 4.50x"
                                  />
                                </CovenantField>
                                <CovenantField>
                                  <CovenantLabel>Type</CovenantLabel>
                                  <CovenantSelect
                                    value={covenantDraft.covenantType}
                                    onChange={(e) =>
                                      setCovenantDraft({
                                        ...covenantDraft,
                                        covenantType: e.target.value as CovenantDraft['covenantType'],
                                      })
                                    }
                                  >
                                    <option value="FINANCIAL">Financial</option>
                                    <option value="AFFIRMATIVE">Affirmative</option>
                                    <option value="NEGATIVE">Negative</option>
                                    <option value="REPORTING">Reporting</option>
                                  </CovenantSelect>
                                </CovenantField>
                                <CovenantField>
                                  <CovenantLabel>Category</CovenantLabel>
                                  <CovenantSelect
                                    value={covenantDraft.covenantCategory}
                                    onChange={(e) =>
                                      setCovenantDraft({
                                        ...covenantDraft,
                                        covenantCategory: e.target.value as CovenantDraft['covenantCategory'],
                                      })
                                    }
                                  >
                                    <option value="">—</option>
                                    <option value="LEVERAGE">Leverage</option>
                                    <option value="COVERAGE">Coverage</option>
                                    <option value="LIQUIDITY">Liquidity</option>
                                    <option value="CAPEX">Capex</option>
                                    <option value="RESTRICTED_PAYMENTS">Restricted payments</option>
                                    <option value="ASSET_SALE">Asset sale</option>
                                    <option value="DEBT_INCURRENCE">Debt incurrence</option>
                                  </CovenantSelect>
                                </CovenantField>
                                <CovenantField>
                                  <CovenantLabel>Metric</CovenantLabel>
                                  <CovenantSelect
                                    value={covenantDraft.metricType}
                                    onChange={(e) =>
                                      setCovenantDraft({
                                        ...covenantDraft,
                                        metricType: e.target.value as CovenantDraft['metricType'],
                                      })
                                    }
                                  >
                                    <option value="">—</option>
                                    <option value="TOTAL_LEVERAGE">Total leverage</option>
                                    <option value="SENIOR_LEVERAGE">Senior leverage</option>
                                    <option value="NET_LEVERAGE">Net leverage</option>
                                    <option value="DEBT_TO_EBITDA">Debt / EBITDA</option>
                                    <option value="INTEREST_COVERAGE">Interest coverage</option>
                                    <option value="FIXED_CHARGE">Fixed charge</option>
                                    <option value="DEBT_SERVICE_COVERAGE">DSCR</option>
                                    <option value="LIQUIDITY">Liquidity</option>
                                    <option value="CAPEX">Capex</option>
                                    <option value="CASH_FLOW">Cash flow</option>
                                    <option value="LOAN_TO_VALUE">LTV</option>
                                    <option value="DEBT_YIELD">Debt yield</option>
                                  </CovenantSelect>
                                </CovenantField>
                                <CovenantField>
                                  <CovenantLabel>Threshold direction</CovenantLabel>
                                  <CovenantSelect
                                    value={covenantDraft.thresholdType}
                                    onChange={(e) =>
                                      setCovenantDraft({
                                        ...covenantDraft,
                                        thresholdType: e.target.value as CovenantDraft['thresholdType'],
                                      })
                                    }
                                  >
                                    <option value="MAX">≤ (MAX)</option>
                                    <option value="MIN">≥ (MIN)</option>
                                    <option value="RANGE">Range</option>
                                  </CovenantSelect>
                                </CovenantField>
                                <CovenantField>
                                  <CovenantLabel>Threshold value</CovenantLabel>
                                  <CovenantInput
                                    value={covenantDraft.thresholdValue}
                                    onChange={(e) =>
                                      setCovenantDraft({ ...covenantDraft, thresholdValue: e.target.value })
                                    }
                                    placeholder="e.g. 4.50"
                                    inputMode="decimal"
                                    style={{ fontFamily: portcoTokens.fontMono }}
                                  />
                                </CovenantField>
                                <CovenantField>
                                  <CovenantLabel>Test frequency</CovenantLabel>
                                  <CovenantSelect
                                    value={covenantDraft.measurementFrequency}
                                    onChange={(e) =>
                                      setCovenantDraft({
                                        ...covenantDraft,
                                        measurementFrequency: e.target
                                          .value as CovenantDraft['measurementFrequency'],
                                      })
                                    }
                                  >
                                    <option value="QUARTERLY">Quarterly</option>
                                    <option value="SEMI_ANNUAL">Semi-annual</option>
                                    <option value="ANNUAL">Annual</option>
                                  </CovenantSelect>
                                </CovenantField>
                              </CovenantFormGrid>
                              {covenantError && <CovenantErr>{covenantError}</CovenantErr>}
                              <CovenantFormFoot>
                                <button
                                  type="button"
                                  onClick={cancelAddCovenant}
                                  style={covenantSecondaryBtnStyle}
                                  disabled={savingCovenant}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={() => submitCovenant(d.id)}
                                  style={covenantPrimaryBtnStyle}
                                  disabled={savingCovenant}
                                >
                                  {savingCovenant ? 'Saving…' : 'Save covenant'}
                                </button>
                              </CovenantFormFoot>
                            </CovenantFormBlock>
                          )}
                          <PanelBody style={{ padding: 0 }}>
                            {covs.length === 0 ? (
                              <Empty>No covenants defined on this facility.</Empty>
                            ) : (
                              <Table>
                                <Thead>
                                  <tr>
                                    <Th>Name</Th>
                                    <Th>Type</Th>
                                    <Th>Metric</Th>
                                    <Th>Frequency</Th>
                                    <Th>Direction</Th>
                                    <Th style={{ textAlign: 'right' }}>Threshold</Th>
                                    <Th style={{ textAlign: 'right' }}>Latest</Th>
                                    <Th>Trend</Th>
                                    <Th>Status</Th>
                                  </tr>
                                </Thead>
                                <tbody>
                                  {covs.map((c) => {
                                    const trend = trendByCovenantId[c.id];
                                    const latest = trend?.history?.[trend.history.length - 1];
                                    const latestStatus = latest?.status;
                                    const statusSeverity: 'BREACHED' | 'WARNING' | 'PASSING' | 'NO_DATA' =
                                      latestStatus === 'BREACHED'
                                        ? 'BREACHED'
                                        : latestStatus === 'WARNING'
                                          ? 'WARNING'
                                          : latestStatus === 'PASSED'
                                            ? 'PASSING'
                                            : c.isActive === false
                                              ? 'NO_DATA'
                                              : 'PASSING';
                                    const statusLabel =
                                      latestStatus
                                        ? latestStatus.charAt(0) + latestStatus.slice(1).toLowerCase()
                                        : c.isActive === false
                                          ? 'Inactive'
                                          : 'Active';
                                    const dir = trend?.direction;
                                    const trendSeverity: 'BREACHED' | 'WARNING' | 'PASSING' | 'NO_DATA' =
                                      dir === 'DETERIORATING'
                                        ? 'WARNING'
                                        : dir === 'IMPROVING' || dir === 'STABLE'
                                          ? 'PASSING'
                                          : 'NO_DATA';
                                    const trendLabel =
                                      dir === 'DETERIORATING'
                                        ? 'Deteriorating'
                                        : dir === 'IMPROVING'
                                          ? 'Improving'
                                          : dir === 'STABLE'
                                            ? 'Stable'
                                            : trend
                                              ? '—'
                                              : '…';
                                    const eta = trend?.estimatedQuartersToBreach ?? null;
                                    return (
                                      <Tr key={c.id}>
                                        <Td>{c.covenantName || `#${c.id}`}</Td>
                                        <Td>{c.covenantType || '—'}</Td>
                                        <Td>{c.metricType || c.covenantCategory || '—'}</Td>
                                        <Td>{c.measurementFrequency || c.testFrequency || '—'}</Td>
                                        <TdMono>{c.thresholdType || c.comparator || '—'}</TdMono>
                                        <TdMono style={{ textAlign: 'right' }}>
                                          {fmtNum(c.thresholdValue, 2)}
                                        </TdMono>
                                        <TdMono style={{ textAlign: 'right' }}>
                                          {latest ? fmtNum(latest.value, 2) : '—'}
                                        </TdMono>
                                        <Td>
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                            <StatusPill $severity={trendSeverity}>{trendLabel}</StatusPill>
                                            {eta != null && (
                                              <span
                                                style={{
                                                  fontSize: 10,
                                                  color: portcoTokens.inkMuted,
                                                  letterSpacing: '0.04em',
                                                }}
                                              >
                                                ETA breach: {eta} qtr{eta === 1 ? '' : 's'}
                                              </span>
                                            )}
                                          </div>
                                        </Td>
                                        <Td>
                                          <StatusPill $severity={statusSeverity}>{statusLabel}</StatusPill>
                                        </Td>
                                      </Tr>
                                    );
                                  })}
                                </tbody>
                              </Table>
                            )}
                          </PanelBody>
                        </Panel>
                      );
                    })
                  )}
                </>
              )}

              {tab === 'documents' && (
                <Panel>
                  <PanelHead>
                    <PanelTitle>Source documents</PanelTitle>
                    <PanelMeta>From document inbox</PanelMeta>
                  </PanelHead>
                  <PanelBody>
                    <Empty>
                      Document linkage surfaces here as the inbox attaches PORTCO_FINANCIALS,
                      PORTCO_DEBT_STATEMENT, and COVENANT_CERTIFICATE_PORTCO documents to this entity.
                    </Empty>
                  </PanelBody>
                </Panel>
              )}
            </BodyNarrow>
          </>
        )}
      </Surface>

      {lifecycleModal && (
        <PortcoDebtLifecycleModal
          action={lifecycleModal.action}
          debt={lifecycleModal.debt}
          onClose={() => setLifecycleModal(null)}
          onSuccess={handleLifecycleSuccess}
        />
      )}

      {markModalOpen && portco && (
        <PortcoMarkModal
          portcoId={portco.id}
          portcoName={portco.companyName}
          onClose={() => setMarkModalOpen(false)}
          onSuccess={handleMarkSuccess}
        />
      )}
    </>
  );
};

export default PortcoDetailScreen;
